import { useRef, useEffect, useState } from 'react';

const services = [
  {
    icon: 'ri-home-4-fill',
    title: 'Residential Home Sales',
    korean: '주택 매매',
    description: 'Expertise in buying and selling single-family homes, townhomes, and condos across Duluth, Suwanee, and Atlanta neighborhoods.',
  },
  {
    icon: 'ri-building-fill',
    title: 'Commercial Real Estate',
    korean: '상업용 부동산',
    description: 'Complete commercial property services including office spaces, retail locations, and mixed-use developments in the Atlanta metro area.',
  },
  {
    icon: 'ri-funds-fill',
    title: 'Investment Properties',
    korean: '투자용 부동산',
    description: 'Strategic investment consulting to help you identify high-yield rental properties, fix-and-flip opportunities, and long-term real estate assets.',
  },
  {
    icon: 'ri-user-heart-fill',
    title: 'Buyer Representation',
    korean: '바이어 에이전트',
    description: 'Dedicated buyer\'s agent services — from property search to negotiation and closing. We fight for your best interests every step of the way.',
  },
  {
    icon: 'ri-price-tag-3-fill',
    title: 'Seller Representation',
    korean: '셀러 에이전트',
    description: 'Maximize your home\'s value with professional staging advice, strategic pricing, and powerful marketing to attract qualified buyers quickly.',
  },
  {
    icon: 'ri-map-2-fill',
    title: 'Relocation Services',
    korean: '이주 서비스',
    description: 'Moving to Atlanta or relocating within Georgia? We provide comprehensive relocation support for families, especially Korean-American communities.',
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 bg-white relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#B8963E]/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#B8963E] text-xs font-bold tracking-widest uppercase mb-3">What We Offer · 서비스</p>
          <h2 className="font-['Playfair_Display'] text-[#1A1A1A] text-3xl lg:text-4xl font-bold mb-4">
            Comprehensive Real Estate Services
          </h2>
          <p className="text-[#666] text-sm max-w-xl mx-auto">
            From your first home purchase to complex commercial investments — we provide expert guidance at every stage.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="group bg-[#F8F6F1] hover:bg-[#B8963E] rounded-lg p-7 transition-all duration-300 cursor-default"
              style={{ transitionDelay: `${index * 60}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-[#B8963E]/15 group-hover:bg-white/20 rounded-lg mb-5 transition-colors duration-300">
                <i className={`${service.icon} text-[#B8963E] group-hover:text-white text-2xl transition-colors duration-300`}></i>
              </div>
              <h3 className="font-['Playfair_Display'] text-[#1A1A1A] group-hover:text-white text-lg font-bold mb-1 transition-colors duration-300">
                {service.title}
              </h3>
              <p className="text-[#B8963E] group-hover:text-white/80 text-xs font-semibold mb-3 transition-colors duration-300">
                {service.korean}
              </p>
              <p className="text-[#555] group-hover:text-white/90 text-sm leading-relaxed transition-colors duration-300">
                {service.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center bg-[#1A1A1A] rounded-lg p-10">
          <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold mb-2">
            Not sure which service fits your needs?
          </h3>
          <p className="text-white/60 text-sm mb-6">
            Contact us for a free, no-obligation consultation. 무료 상담 문의를 환영합니다.
          </p>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 bg-[#B8963E] hover:bg-[#A07830] text-white px-8 py-3 rounded-md text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-message-3-fill"></i>
            Get Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
